import { fetchAndActivate, getValue } from "firebase/remote-config";
import { getRemoteConfigInstance } from "../firebaseConfig";

const REMOTE_CONFIG_PRICING_KEY = "ticket_pricing_config";

const DEFAULT_PRICING_CONFIG = {
  localTourismPricing: {
    GIA1: {
      propio: { hasta20: 3, mas20: 2 },
      municipal: { hasta20: 4, mas20: 3 },
    },
    GIA2: {
      propio: { hasta20: 5, mas20: 4 },
      municipal: { hasta20: 7, mas20: 6 },
    },
  },
  pricingMatrix: {
    CAB: {
      general: 5,
      pensionista: 3,
      grupo: 2,
      centroDocente: 2,
    },
    ANH: {
      general: 1.5,
      pensionista: 1,
      grupo: 1,
      centroDocente: 1,
    },
    GIA1: {
      general: 7,
      pensionista: 5,
      grupo: 4,
      centroDocente: 4,
    },
    GIA2: {
      general: 12,
      pensionista: 8,
      grupo: 6,
      centroDocente: 6,
    },
  },
};

let cachedPricingConfig = DEFAULT_PRICING_CONFIG;
let hasLoadedPricingConfig = false;

const cloneDefaultPricing = () =>
  JSON.parse(JSON.stringify(DEFAULT_PRICING_CONFIG));

const hasValidConfigShape = (value) => {
  if (!value || typeof value !== "object") {
    return false;
  }

  return Boolean(value.pricingMatrix && value.localTourismPricing);
};

const parsePricingConfig = (rawValue) => {
  try {
    const parsed = JSON.parse(rawValue);

    if (!hasValidConfigShape(parsed)) {
      return cloneDefaultPricing();
    }

    return parsed;
  } catch (error) {
    return cloneDefaultPricing();
  }
};

const normalizeVisitCode = (visita, availableVisits = []) => {
  if (!visita || typeof visita !== "string") {
    return visita;
  }

  if (visita.length <= 4) {
    return visita;
  }

  return (
    availableVisits.find((visit) => visit.label === visita)?.value || visita
  );
};

export const getCurrentPricingConfig = () => cachedPricingConfig;

export const loadPricingConfig = async () => {
  if (hasLoadedPricingConfig) {
    return cachedPricingConfig;
  }

  const remoteConfig = await getRemoteConfigInstance();

  if (!remoteConfig) {
    hasLoadedPricingConfig = true;
    return cachedPricingConfig;
  }

  remoteConfig.defaultConfig = {
    [REMOTE_CONFIG_PRICING_KEY]: JSON.stringify(cloneDefaultPricing()),
  };

  try {
    await fetchAndActivate(remoteConfig);
    const remoteValue = getValue(
      remoteConfig,
      REMOTE_CONFIG_PRICING_KEY,
    ).asString();
    cachedPricingConfig = parsePricingConfig(remoteValue);
  } catch (error) {
    cachedPricingConfig = cloneDefaultPricing();
  }

  hasLoadedPricingConfig = true;
  return cachedPricingConfig;
};

export const calculateTicketPrice = (
  formValues,
  availableVisits = [],
  pricingConfig = cachedPricingConfig,
) => {
  let { tipoEntrada, visita, grupoSize, empresaLocal, guia } = formValues;

  const parsedGroupSize = parseInt(grupoSize, 10);
  visita = normalizeVisitCode(visita, availableVisits);

  if (!visita || !parsedGroupSize || parsedGroupSize <= 0) {
    return 0;
  }

  if (empresaLocal === true && (visita === "GIA1" || visita === "GIA2")) {
    const grupoCategoria = parsedGroupSize <= 20 ? "hasta20" : "mas20";
    const localTourismPricing = pricingConfig.localTourismPricing || {};

    if (!localTourismPricing[visita]?.[guia]) {
      return 0;
    }

    const basePrice = localTourismPricing[visita][guia][grupoCategoria] || 0;
    return (basePrice * parsedGroupSize).toFixed(2);
  }

  const pricingMatrix = pricingConfig.pricingMatrix || {};
  const basePrice = pricingMatrix[visita]?.[tipoEntrada] || 0;

  return (basePrice * parsedGroupSize).toFixed(2);
};

export { DEFAULT_PRICING_CONFIG, REMOTE_CONFIG_PRICING_KEY };
